import { apiGet, apiPatch } from '../client'

export type Industry =
  | 'TECHNOLOGY'
  | 'HEALTHCARE'
  | 'FINANCE'
  | 'EDUCATION'
  | 'MANUFACTURING'
  | 'RETAIL'
  | 'CONSULTING'
  | 'MEDIA_AND_ENTERTAINMENT'
  | 'GOVERNMENT'
  | 'NON_PROFIT'
  | 'REAL_ESTATE'
  | 'LEGAL'
  | 'ENERGY'
  | 'TRANSPORTATION'
  | 'HOSPITALITY'
  | 'AGRICULTURE'
  | 'CONSTRUCTION'
  | 'TELECOMMUNICATIONS'
  | 'PHARMACEUTICALS'
  | 'AEROSPACE_AND_DEFENSE'
  | 'OTHER'

export type SponsorshipStatus = 'YES' | 'NO' | 'SOMETIMES' | 'UNSURE'

export const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'] as const

// Shared by onboarding and the profile editor. Any answer counts toward a
// complete profile - employers don't have to sponsor to finish it.
export const SPONSORING_OPTIONS: { label: string; value: SponsorshipStatus }[] = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
  { label: 'Case-by-case', value: 'SOMETIMES' },
  { label: 'Unsure / need guidance', value: 'UNSURE' },
]
export type H1BSponsorshipType = 'YES_ALWAYS' | 'YES_FOR_EXCEPTIONAL_CANDIDATES' | 'NO' | 'CURRENTLY_ON_HOLD'
export type VerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED'

export interface EmployerProfile {
  [key: string]: unknown
  avatarUrl?: string
  companyDescription?: string
  companyName: string
  companySize?: string | null
  companyWebsite: string
  currentlySponsoring: SponsorshipStatus
  department?: string
  firstName: string
  h1bSponsorship: H1BSponsorshipType
  id: string
  industry: Industry
  lastName: string
  phoneNumber?: string
  title?: string
  verificationStatus: VerificationStatus
}

export interface UpdateEmployerProfilePayload {
  avatarUrl?: string
  companyDescription?: string
  companyName?: string
  companySize?: string | null
  companyWebsite?: string
  currentlySponsoring?: SponsorshipStatus
  department?: string
  firstName?: string
  h1bSponsorship?: H1BSponsorshipType
  industry?: Industry
  lastName?: string
  phoneNumber?: string
  title?: string
}

export interface EmployerDashboardStats {
  applications: {
    pending: number
    total: number
  }
  interviews: {
    scheduled: number
  }
  jobs: {
    active: number
    draft: number
    total: number
  }
}

export const getEmployerProfile = () => apiGet<EmployerProfile>('/employer/profile')

export const updateEmployerProfile = (payload: UpdateEmployerProfilePayload) => apiPatch<EmployerProfile>('/employer/profile', payload)

export const getEmployerDashboardStats = () => apiGet<EmployerDashboardStats>('/employer/dashboard/stats')

export interface PublicEmployerJob {
  applicationDeadline?: string
  createdAt: string
  employmentType?: string
  id: string
  isRemote?: boolean
  location?: string
  sponsorshipAvailable?: boolean
  title: string
}

export interface PublicEmployerEvent {
  id: string
  isVirtual: boolean
  location?: string | null
  startAt: string
  title: string
  type: string
}

export interface PublicEmployerReview {
  comment?: string | null
  createdAt: string
  id: string
  raterRole: 'STUDENT' | 'SCHOOL_ADMIN'
  stars: number
}

export interface PublicEmployerProfile extends EmployerProfile {
  _count?: { jobs: number }
  city?: string | null
  events?: PublicEmployerEvent[]
  institution?: { city?: string | null; logoUrl?: string | null; name: string; state?: string | null } | null
  jobs?: PublicEmployerJob[]
  rating?: { average: number | null; count: number }
  reviews?: PublicEmployerReview[]
  state?: string | null
}

export const getPublicEmployerProfile = (employerProfileId: string) => apiGet<PublicEmployerProfile>(`/profiles/employer/${employerProfileId}`)

/** 403s with a reason/upgradeUrl until the caller is eligible (applied to one
 * of this employer's jobs, or on a Basic/Pro plan) - see the backend's
 * SubscriptionGatingService.canViewEmployerContactInfo for the exact rule. */
export const getEmployerContactInfo = (employerProfileId: string) => apiGet<{ phoneNumber?: string }>(`/profiles/employer/${employerProfileId}/contact`)
